<template>
  <div class="login-page">
    <div class="left-side">
      <a href="/" class="back-link">&lt; back</a>
      <img
        :src="theme.current.loginImage"
        alt="Captain Logo"
        class="captain-logo"
      />
    </div>
    <div class="right-side">
      <img
        :src="theme.current.loginImage"
        alt="Captain Logo"
        class="captain-logo-mobile"
      />
      <!-- a href="/" class="back-link">&lt; back</a -->
      <h1>Account Login</h1>
      <p>
        If you are already a member you can login with your email and password.
      </p>
      <Form @submit="handleLogin">
        <div>
          <label for="username">Email Address</label>
          <input type="text" id="username" v-model="username" />
          <span class="error-message">{{ usernameError }}</span>
        </div>
        <div>
          <label for="password">Password</label>
          <input type="password" id="password" v-model="password" />
          <span class="error-message">{{ passwordError }}</span>
        </div>
        <div class="remember-me">
          <input type="checkbox" id="rememberMe" v-model="rememberMe" />
          <label for="rememberMe">Remember Me</label>
        </div>

        <!-- Add forgot password link -->
        <div class="forgot-password-container">
          <a href="/reset-password" class="forgot-password-link"
            >Forgot Password?</a
          >
        </div>

        <button type="submit" v-if="waiting == false">Login</button>
        <button type="submit" v-if="waiting == true" disabled>
          <img class="svg" src="../assets/site-wide/loading.svg" />
        </button>
        <div v-if="errorMessage" class="error">{{ errorMessage }}</div>
      </Form>
      <div class="signup-container" v-if="false">
        <!-- signups disabled -->
        <span>Don't have an account? </span>
        <a href="/signup" class="signup-link">Sign up</a>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import { useForm, useField, Form } from "vee-validate";
import * as yup from "yup";
import { useSessionStore } from "@/stores/session";
import { useRouter, useRoute } from "vue-router";
import authApi from "@/services/auth_api";
import { useThemeStore } from "@/stores/theme";
import swal from "@/services/swal";

export default {
  name: "LoginPage",
  components: {
    Form,
  },
  setup() {
    const rememberMe = ref(false);
    const waiting = ref(false);
    const session = useSessionStore();
    const router = useRouter();
    const route = useRoute();

    const theme = useThemeStore();

    const schema = yup.object({
      username: yup
        .string()
        .required("Email is required")
        .email("Must be a valid email"),
      password: yup
        .string()
        .required("Password is required")
        .min(6, "Password must be at least 6 characters")
        .max(20, "Password must be at most 20 characters")
        .matches(
          /^[a-zA-Z0-9]{6,20}$/,
          "Password must contain only letters and numbers",
        ),
    });

    const { handleSubmit } = useForm({
      validationSchema: schema,
    });

    const { value: username, errorMessage: usernameError } =
      useField("username");
    const { value: password, errorMessage: passwordError } =
      useField("password");

    const handleLogin = handleSubmit(async (values) => {
      // console.log('Form values:', values);
      waiting.value = true;
      let response;

      try {
        response = await authApi.login(
          {
            username: values.username,
            password: values.password,
          },
          false, // pass force flag
        );
        console.log(`LoginPage.vue, handleLogin, Fired the request`);
      } catch (error) {
        console.log(
          `LoginPage.vue, handleLogin, error.response?.status: ${error.response?.status}`,
        );
        // 2) If server says "already_logged_in_elsewhere", ask user
        if (
          error.response?.status === 409 &&
          error.response.data?.errors?.[0]?.message ===
            "already_logged_in_elsewhere"
        ) {
          const { isConfirmed } = await swal.fire({
            icon: "warning",
            text: "You are logged in on another device. Log out there and continue here?",
            showCancelButton: true,
            confirmButtonText: "Continue",
            cancelButtonText: "Cancel",
          });
          if (isConfirmed) {
            // 3) Retry with force=true
            response = await authApi.login(
              {
                username: values.username,
                password: values.password,
              },
              true, // pass force flag
            );
          } else {
            // Set response as a message
            response = "Login cancelled";
          }
        } else {
          // 4) Other errors
          console.error("Login error:", error);
          console.error("Error response:", error.response);
          console.error("Error message:", error.message);
          const errorMsg =
            error.response?.data?.errors?.[0]?.message ||
            error.response?.data?.message ||
            error.message ||
            "Unknown Error!";
          swal.fire({ icon: "error", text: `Login failed: ${errorMsg}` });
          response = errorMsg;
        }
      }

      if (typeof response === "string") {
        // If the response is a message
        console.error("Login failed:", response);
        router.push({ path: "/login" });
      } else {
        // For the correct response
        session.logout();
        await session.login(
          values.username,
          response.roles,
          response.firstname,
          response.lastname,
          response.access_token,
          response.refresh_token,
          response.group_name ?? null,
          response.send_sms ?? false,
          response.send_email ?? true,
          response.profile_photo ?? null,
          response.phone_number ?? null,
          response.enable_session_manipulation ?? false,
        );

        // Override theme by account group
        theme.setThemeByGroup(response.group_name);
        theme.applyCssVarsToRoot();

        const target = (() => {
          if (session.rolesIncludes("admin")) return "/users";
          if (session.rolesIncludes("mentor")) return "/mentees";
          if (session.rolesIncludes("mentee") || session.rolesIncludes("user"))
            return "/chat";
          return "/";
        })();

        await router.replace(target);
      }
      waiting.value = false;
    });

    return {
      theme,
      username,
      usernameError,
      password,
      passwordError,
      rememberMe,
      waiting,
      handleLogin,
      errorMessage: route.query.error,
    };
  },
};
</script>

<style scoped>
.login-page {
  display: flex;
  height: var(--app-height);
  min-height: 100vh; /* Fallback for browsers that don't support CSS variables */
  min-height: -webkit-fill-available; /* iOS Safari specific */
  margin: 0;
}

.left-side,
.right-side {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.left-side {
  background-color: var(--landing-color);
  flex: 1;
}

.right-side {
  background-color: var(--landing-color);
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1.5;
}

.right-side h1,
.right-side p,
.right-side form,
.right-side div {
  width: 50%;
  text-align: left;
}

.login-page h1 {
  font-size: 2rem;
  color: #333333;
  margin-bottom: 0.5em;
}

.login-page form {
  display: flex;
  flex-direction: column;
  align-items: left;
  width: 50%;
}

.login-page div {
  margin-bottom: 1em;
  width: 100%;
}

.login-page label {
  margin-bottom: 0.5em;
  color: #333333;
  width: 50%;
  text-align: left;
}

.login-page input {
  padding: 0.5em;
  font-size: 1em;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
}

.remember-me {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  margin-left: 0;
  margin-top: 0;
  margin-bottom: 0;
}

.remember-me input {
  margin-right: 0.5em;
  margin-left: 0;
  margin-top: 0;
  width: 10%;
  margin-bottom: 0;
}

.login-page button {
  padding: 0.7em;
  font-size: 1em;
  color: #fff;
  background-color: var(--primary-color);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  width: 100%;
}

.login-page button:hover {
  background-color: var(--primary-color);
}

.login-page img.svg {
  fill: currentColor;
}

.back-link {
  position: absolute;
  top: 20px;
  left: 40px;
  text-decoration: none;
  color: var(--primary-color);
  font-size: 20px;
}

.back-link:hover {
  text-decoration: underline;
}

.signup-container {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.signup-container span {
  margin-right: 5px;
  /* Add space between span and a */
}

.signup-container a {
  display: inline;
}

.signup-link {
  text-decoration: none;
  color: #007bff;
  font-size: 16px;
}

.signup-link:hover {
  text-decoration: underline;
}

.back-link {
  margin-bottom: 20px;
}

.captain-logo-mobile {
  display: none;
}

h1 {
  font-size: 2em;
  /* Default font size for larger screens */
}

label {
  font-size: 1em;
  /* Default font size for labels */
}

p {
  font-size: 1em;
  /* Default font size for paragraphs */
}

/* Media query to hide the left-side div on mobile devices */
@media (max-width: 600px) {
  .left-side {
    display: none;
  }

  .right-side {
    flex: 1;
    padding: 0 1.5em; /* Add padding for mobile */
  }

  .right-side h1,
  .right-side p,
  .right-side form,
  .right-side div {
    width: 100%; /* Override 50% width for mobile */
  }

  .login-page form {
    width: 100%;
  }

  .back-link {
    order: 1;
    margin-bottom: 10px;
  }

  .captain-logo-mobile {
    display: block;
    width: 50%;
    height: auto;
    margin-bottom: 1em;
    margin-top: 20%;
  }

  h1 {
    font-size: 1.5em;
    /* Smaller font size for mobile devices */
  }

  label {
    font-size: 0.675em;
    /* Smaller font size for labels on mobile devices */
  }

  p {
    font-size: 0.9em;
    /* Smaller font size for paragraphs on mobile devices */
  }

  .login-page input {
    font-size: 0.9em;
    padding: 0.4em;
  }

  .remember-me input {
    width: auto;
  }

  .login-page button {
    padding: 0.6em;
    font-size: 0.9em;
  }
}

.forgot-password-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1em;
  margin-top: -0.5em;
}

.forgot-password-link {
  text-decoration: none;
  color: #007bff;
  font-size: 14px;
}

.forgot-password-link:hover {
  text-decoration: underline;
}
</style>
